import { useContext, useMemo } from 'react';
import { AuthContext } from '../context/AuthContext';

// All permission constants based on your backend permissions
export const PERMISSIONS = {
  // Products
  ADD_PRODUCTS: 'add_products',
  EDIT_PRODUCTS: 'edit_products',
  VIEW_PRODUCTS: 'view_products',
  
  // Staff
  ADD_STAFF: 'add_staff',
  EDIT_STAFF: 'edit_staff',
  
  // User Management
  ADD_ROLE: 'add_role',
  EDIT_PERMISSIONS: 'edit_permissions',
  USER_MANAGEMENT: 'user_management',
  
  // Brokers
  VIEW_BROKERS: 'view_brokers',
  ADD_BROKERS: 'add_brokers',
  EDIT_BROKERS: 'edit_brokers',
  
  // Vendors
  VIEW_VENDORS: 'view_vendors',
  ADD_VENDORS: 'add_vendors',
  EDIT_VENDORS: 'edit_vendors',
  
  // Buyers
  VIEW_BUYERS: 'view_buyers',
  ADD_BUYERS: 'add_buyers',
  EDIT_BUYERS: 'edit_buyers',
  
  // Sales Orders
  SO_VIEW: 'so_view',
  SO_CREATE: 'so_create',
  SO_EDIT: 'so_edit',
  SO_DELETE: 'so_delete',

  // In-House Sales Orders
  SO_INHOUSE_VIEW: 'so_inhouse_view',
  SO_INHOUSE_CREATE: 'so_inhouse_create',
  SO_INHOUSE_EDIT: 'so_inhouse_edit',
  SO_INHOUSE_DELETE: 'so_inhouse_delete',
  
  // Purchase Orders
  PO_VIEW: 'po_view',
  PO_CREATE: 'po_create',
  PO_EDIT: 'po_edit',
  PO_DELETE: 'po_delete',
  
  // GRN
  GRN_CREATE: 'grn_create',
  
  // Quality Check
  QUALITY_CHECK_CREATE: 'quality_check_create',
  
  // Unloading
  UNLOADING_ENTRY_ADD: 'unloading_entry_add',
  
  // Returns
  RETURN_NOTE_CREATE: 'return_note_create',
  
  // Purchase Documents
  PO_DOC_ADD: 'po_doc_add',
  
  // Purchase Payments
  PO_PAYMENT_ADD: 'po_payment_add',
  
  // Purchase Requests
  PO_REQUEST_ADD: 'po_request_add',
  PO_REQUEST_EDIT: 'po_request_edit',
  PO_REQUEST_VIEW: 'po_request_view',
  
  // Quotations
  QUOTATION_VIEW: 'quotation_view',
  QUOTATION_CREATE: 'quotation_create',
  QUOTATION_EDIT: 'quotation_edit',
  
  // BOM
  BOM_CREATE: 'bom_create',
  BOM_VIEW: 'bom_view',
  BOM_EDIT: 'bom_edit',
  
  // Production Batch
  PRODUCTION_BATCH_ADD: 'production_batch_add',
  PRODUCTION_BATCH_VIEW: 'production_batch_view',
  PRODUCTION_BATCH_EDIT: 'production_batch_edit',
  
  // Production Request
  PRODUCTION_REQUEST_CREATE: 'production_request_create',
  PRODUCTION_REQUEST_VIEW: 'production_request_view',
  PRODUCTION_REQUEST_EDIT: 'production_request_edit',
  
  // Machines
  MACHINE_MASTER_ADD: 'machine_master_add',
  MACHINE_MASTER_EDIT: 'machine_master_edit',
  MACHINE_MASTER_VIEW: 'machine_master_view',
  
  // Plants
  PLANT_MASTER_ADD: 'plant_master_add',
  PLANT_MASTER_VIEW: 'plant_master_view',
  PLANT_MASTER_EDIT: 'plant_master_edit',
  
  // Warehouse
  WAREHOUSE_ADD: 'warehouse_add',
  WAREHOUSE_VIEW: 'warehouse_view',
  WAREHOUSE_EDIT: 'warehouse_edit',
  
  // Packaging Material
  PACKAGING_MATERIAL_ADD: 'packaging_material_add',
  PACKAGING_MATERIAL_VIEW: 'packaging_material_view',
  PACKAGING_MATERIAL_EDIT: 'packaging_material_edit',
  
  // Settings
  SETTINGS_VIEW: 'settings_view',
  SETTINGS_EDIT: 'settings_edit',
  
  // Packing Materials
  PM_PO_VIEW: 'pm_po_view',
  PM_PO_CREATE: 'pm_po_create',
  PM_PO_EDIT: 'pm_po_edit',
  PM_PO_DELETE: 'pm_po_delete',
  
  // Inventory
  INVENTORY_VIEW: 'inventory_view',
  INVENTORY_EDIT: 'inventory_edit',
  
  // Stock
  STOCK_VIEW: 'stock_view',
  STOCK_EDIT: 'stock_edit',
  
  // Packing Operations
  PACKING_OPERATIONS_VIEW: 'packing_operations_view',
  PACKING_OPERATIONS_CREATE: 'packing_operations_create',
  PACKING_OPERATIONS_EDIT: 'packing_operations_edit',
  
  // Dispatch
  DISPATCH_VIEW: 'dispatch_view',
  DISPATCH_CREATE: 'dispatch_create',
  DISPATCH_EDIT: 'dispatch_edit',
  
  // Groups
  GROUPS_VIEW: 'groups_view',
  GROUPS_ADD: 'groups_add',
  GROUPS_EDIT: 'groups_edit',
  GROUPS_DELETE: 'groups_delete',

  // Labs
  LABS_VIEW: 'lab_view',
  LABS_ADD: 'lab_add',
  LABS_EDIT: 'lab_edit',
  LABS_DELETE: 'lab_delete',

  // Collectors
  COLLECTORS_VIEW: 'collector_view',
  COLLECTORS_ADD: 'collector_add',
  COLLECTORS_EDIT: 'collector_edit',
  COLLECTORS_DELETE: 'collector_delete',
  
  // Additional permissions for existing models
  DELETE_PRODUCTS: 'delete_products',
  VIEW_STAFF: 'view_staff',
  DELETE_STAFF: 'delete_staff',
  DELETE_BROKERS: 'delete_brokers',
  DELETE_VENDORS: 'delete_vendors',
  DELETE_BUYERS: 'delete_buyers',
  VIEW_ROLES: 'view_roles',
  EDIT_ROLES: 'edit_roles',
  DELETE_ROLES: 'delete_roles',
  WAREHOUSE_DELETE: 'warehouse_delete',
  PACKAGING_MATERIAL_DELETE: 'packaging_material_delete',
  MACHINE_MASTER_DELETE: 'machine_master_delete',
  PLANT_MASTER_DELETE: 'plant_master_delete',
  BOM_DELETE: 'bom_delete',
  PRODUCTION_BATCH_DELETE: 'production_batch_delete',
  PRODUCTION_REQUEST_DELETE: 'production_request_delete',
  QUOTATION_DELETE: 'quotation_delete',
  PO_REQUEST_DELETE: 'po_request_delete',
  PO_PAYMENT_VIEW: 'po_payment_view',
  PO_PAYMENT_EDIT: 'po_payment_edit',
  PO_DOC_VIEW: 'po_doc_view',
  PO_DOC_EDIT: 'po_doc_edit',
  RETURN_NOTE_VIEW: 'return_note_view',
  RETURN_NOTE_EDIT: 'return_note_edit',
  UNLOADING_ENTRY_VIEW: 'unloading_entry_view',
  UNLOADING_ENTRY_EDIT: 'unloading_entry_edit',
  QUALITY_CHECK_VIEW: 'quality_check_view',
  QUALITY_CHECK_EDIT: 'quality_check_edit',
  GRN_VIEW: 'grn_view',
  GRN_EDIT: 'grn_edit',
  SO_PAYMENT_VIEW: 'so_payment_view',
  SO_PAYMENT_ADD: 'so_payment_add',
  SO_PAYMENT_EDIT: 'so_payment_edit',
};

// Permission categories for organization
export const PERMISSION_CATEGORIES = {
  PRODUCTS: 'products',
  STAFF: 'staff',
  USER_MANAGEMENT: 'user_management',
  BROKERS: 'brokers',
  VENDORS: 'vendors',
  BUYERS: 'buyers',
  SALES_ORDERS: 'sales_orders',
  PURCHASE_ORDERS: 'purchase_orders',
  GRN: 'grn',
  QUALITY_CHECK: 'quality_check',
  UNLOADING: 'unloading',
  RETURNS: 'returns',
  PURCHASE_DOCUMENTS: 'purchase_documents',
  PURCHASE_PAYMENTS: 'purchase_payments',
  PURCHASE_REQUESTS: 'purchase_requests',
  QUOTATIONS: 'quotations',
  BOM: 'bom',
  PRODUCTION_BATCH: 'production_batch',
  PRODUCTION_REQUEST: 'production_request',
  MACHINES: 'machines',
  PLANTS: 'plants',
  WAREHOUSE: 'warehouse',
  PACKAGING_MATERIAL: 'packaging_material',
  PACKING_MATERIALS: 'packing_materials',
  SETTINGS: 'settings',
  INVENTORY: 'inventory',
  STOCK: 'stock',
  PACKING_OPERATIONS: 'packing_operations',
  DISPATCH: 'dispatch',
  GROUPS: 'groups',
  SALES_ORDER_PAYMENTS: 'sales_order_payments'
};

// Safely read permissions from user (supports user.roleId populated or flat user.permissions)
export function getUserPermissions(user) {
  if (!user) return [];
  
  // Try to get permissions from populated roleId first
  const rolePerms = Array.isArray(user?.roleId?.permissions) ? user.roleId.permissions : [];
  
  // Fallback to flat permissions on user
  const flatPerms = Array.isArray(user?.permissions) ? user.permissions : [];
  
  // Combine and deduplicate
  const all = [...rolePerms, ...flatPerms].filter(Boolean);
  return Array.from(new Set(all));
}

// Hook to get user permissions
export function usePermissions() {
  const { user, loggedUser } = useContext(AuthContext);
  
  // Support both user and loggedUser for backward compatibility
  const currentUser = user || loggedUser;
  
  const perms = useMemo(() => getUserPermissions(currentUser), [currentUser]);
  return perms;
}

// Hook to check if user has specific permission(s)
export function useHasPermission(required, options = { mode: 'any' }) {
  const perms = usePermissions();
  
  if (!required) return true;
  
  const list = Array.isArray(required) ? required : [required];
  if (!list.length) return true;

  if (options.mode === 'all') {
    return list.every(p => perms.includes(p));
  }
  
  // Default mode is 'any'
  return list.some(p => perms.includes(p));
}

// Utility function to check permissions outside of React components
export function hasPermission(user, required, options = { mode: 'any' }) {
  const perms = getUserPermissions(user);
  
  if (!required) return true;
  
  const list = Array.isArray(required) ? required : [required];
  if (!list.length) return true;

  if (options.mode === 'all') {
    return list.every(p => perms.includes(p));
  }
  
  return list.some(p => perms.includes(p));
}

// Helper function to group permissions by category for UI display
export function groupPermissionsByCategory(permissions) {
  const grouped = {};
  
  permissions.forEach(permission => {
    // Extract category from permission name or use a mapping
    let category = 'other';
    
    // Map permission names to categories based on your backend structure
    if (permission.includes('product')) category = PERMISSION_CATEGORIES.PRODUCTS;
    else if (permission.includes('staff')) category = PERMISSION_CATEGORIES.STAFF;
    else if (permission.includes('role') || permission.includes('user_management')) category = PERMISSION_CATEGORIES.USER_MANAGEMENT;
    else if (permission.includes('broker')) category = PERMISSION_CATEGORIES.BROKERS;
    else if (permission.includes('vendor')) category = PERMISSION_CATEGORIES.VENDORS;
    else if (permission.includes('buyer')) category = PERMISSION_CATEGORIES.BUYERS;
    else if (permission.includes('so_')) category = PERMISSION_CATEGORIES.SALES_ORDERS;
    else if (permission.includes('po_') && !permission.includes('request')) category = PERMISSION_CATEGORIES.PURCHASE_ORDERS;
    else if (permission.includes('grn_')) category = PERMISSION_CATEGORIES.GRN;
    else if (permission.includes('quality_check')) category = PERMISSION_CATEGORIES.QUALITY_CHECK;
    else if (permission.includes('unloading')) category = PERMISSION_CATEGORIES.UNLOADING;
    else if (permission.includes('return_note')) category = PERMISSION_CATEGORIES.RETURNS;
    else if (permission.includes('po_doc')) category = PERMISSION_CATEGORIES.PURCHASE_DOCUMENTS;
    else if (permission.includes('po_payment')) category = PERMISSION_CATEGORIES.PURCHASE_PAYMENTS;
    else if (permission.includes('po_request') || permission.includes('request')) category = PERMISSION_CATEGORIES.PURCHASE_REQUESTS;
    else if (permission.includes('quotation')) category = PERMISSION_CATEGORIES.QUOTATIONS;
    else if (permission.includes('bom_')) category = PERMISSION_CATEGORIES.BOM;
    else if (permission.includes('production_batch')) category = PERMISSION_CATEGORIES.PRODUCTION_BATCH;
    else if (permission.includes('production_request')) category = PERMISSION_CATEGORIES.PRODUCTION_REQUEST;
    else if (permission.includes('machine')) category = PERMISSION_CATEGORIES.MACHINES;
    else if (permission.includes('plant')) category = PERMISSION_CATEGORIES.PLANTS;
    else if (permission.includes('warehouse')) category = PERMISSION_CATEGORIES.WAREHOUSE;
    else if (permission.includes('packaging_material')) category = PERMISSION_CATEGORIES.PACKAGING_MATERIAL;
    
    if (!grouped[category]) {
      grouped[category] = [];
    }
    grouped[category].push(permission);
  });
  
  return grouped;
}

// Common permission combinations for easier access
export const COMMON_PERMISSIONS = {
  // Full CRUD operations
  PRODUCTS_FULL: [PERMISSIONS.VIEW_PRODUCTS, PERMISSIONS.ADD_PRODUCTS, PERMISSIONS.EDIT_PRODUCTS],
  BROKERS_FULL: [PERMISSIONS.VIEW_BROKERS, PERMISSIONS.ADD_BROKERS, PERMISSIONS.EDIT_BROKERS],
  VENDORS_FULL: [PERMISSIONS.VIEW_VENDORS, PERMISSIONS.ADD_VENDORS, PERMISSIONS.EDIT_VENDORS],
  BUYERS_FULL: [PERMISSIONS.VIEW_BUYERS, PERMISSIONS.ADD_BUYERS, PERMISSIONS.EDIT_BUYERS],
  
  // Sales Orders
  SALES_ORDERS_FULL: [PERMISSIONS.SO_VIEW, PERMISSIONS.SO_CREATE, PERMISSIONS.SO_EDIT, PERMISSIONS.SO_DELETE],
  SALES_ORDERS_READ: [PERMISSIONS.SO_VIEW],
  
  // Purchase Orders
  PURCHASE_ORDERS_FULL: [PERMISSIONS.PO_VIEW, PERMISSIONS.PO_CREATE, PERMISSIONS.PO_EDIT, PERMISSIONS.PO_DELETE],
  PURCHASE_ORDERS_READ: [PERMISSIONS.PO_VIEW],
  
  // Purchase Requests
  PURCHASE_REQUESTS_FULL: [PERMISSIONS.PO_REQUEST_VIEW, PERMISSIONS.PO_REQUEST_ADD, PERMISSIONS.PO_REQUEST_EDIT],
  PURCHASE_REQUESTS_READ: [PERMISSIONS.PO_REQUEST_VIEW],
  
  // Production
  PRODUCTION_FULL: [
    PERMISSIONS.BOM_VIEW, PERMISSIONS.BOM_CREATE, PERMISSIONS.BOM_EDIT,
    PERMISSIONS.PRODUCTION_BATCH_VIEW, PERMISSIONS.PRODUCTION_BATCH_ADD, PERMISSIONS.PRODUCTION_BATCH_EDIT,
    PERMISSIONS.PRODUCTION_REQUEST_VIEW, PERMISSIONS.PRODUCTION_REQUEST_CREATE, PERMISSIONS.PRODUCTION_REQUEST_EDIT
  ],
  
  // Machines and Plants
  MACHINES_FULL: [PERMISSIONS.MACHINE_MASTER_VIEW, PERMISSIONS.MACHINE_MASTER_ADD, PERMISSIONS.MACHINE_MASTER_EDIT],
  PLANTS_FULL: [PERMISSIONS.PLANT_MASTER_VIEW, PERMISSIONS.PLANT_MASTER_ADD, PERMISSIONS.PLANT_MASTER_EDIT],
  
  // Warehouse
  WAREHOUSE_FULL: [PERMISSIONS.WAREHOUSE_VIEW, PERMISSIONS.WAREHOUSE_ADD, PERMISSIONS.WAREHOUSE_EDIT],
  
  // Admin permissions (user management)
  ADMIN_PERMISSIONS: [PERMISSIONS.ADD_ROLE, PERMISSIONS.EDIT_PERMISSIONS, PERMISSIONS.USER_MANAGEMENT],
  
  // Settings permissions
  SETTINGS_PERMISSIONS: [PERMISSIONS.SETTINGS_VIEW, PERMISSIONS.SETTINGS_EDIT],
  
  // Packing Materials permissions
  PACKING_MATERIALS_FULL: [PERMISSIONS.PM_PO_VIEW, PERMISSIONS.PM_PO_CREATE, PERMISSIONS.PM_PO_EDIT, PERMISSIONS.PM_PO_DELETE],
  PACKING_MATERIALS_READ: [PERMISSIONS.PM_PO_VIEW],
  
  // Inventory permissions
  INVENTORY_FULL: [PERMISSIONS.INVENTORY_VIEW, PERMISSIONS.INVENTORY_EDIT],
  
  // Stock permissions
  STOCK_FULL: [PERMISSIONS.STOCK_VIEW, PERMISSIONS.STOCK_EDIT],
  
  // Packing Operations permissions
  PACKING_OPERATIONS_FULL: [PERMISSIONS.PACKING_OPERATIONS_VIEW, PERMISSIONS.PACKING_OPERATIONS_CREATE, PERMISSIONS.PACKING_OPERATIONS_EDIT],
  
  // Dispatch permissions
  DISPATCH_FULL: [PERMISSIONS.DISPATCH_VIEW, PERMISSIONS.DISPATCH_CREATE, PERMISSIONS.DISPATCH_EDIT],
  
  // Groups permissions
  GROUPS_FULL: [PERMISSIONS.GROUPS_VIEW, PERMISSIONS.GROUPS_ADD, PERMISSIONS.GROUPS_EDIT, PERMISSIONS.GROUPS_DELETE]
};

const permissionsUtils = {
  PERMISSIONS,
  PERMISSION_CATEGORIES,
  COMMON_PERMISSIONS,
  getUserPermissions,
  usePermissions,
  useHasPermission,
  hasPermission,
  groupPermissionsByCategory
};

export default permissionsUtils;
